import { useEffect, useMemo, useState, useSyncExternalStore } from 'react';
import { NO_CALENDAR } from '../../lib/calendar-visibility';
import { Plus, Check, Trash2 } from '../../icons';
import { layerConfirm } from '../../components/LayerPopup/LayerPopup';
import {
  listCalendars, createCalendar, deleteCalendar, patchCalendar,
  TYPE_COLOR_MAP,
  type CalendarDto, type CalendarEventDto, type CalendarEventType,
} from '../../api/calendar';
import { useCalendarStore } from '../../store/calendarStore';
import { useAuthStore } from '../../store/authStore';
import { listTeams, type TeamDto } from '../../api/project-teams';
import { tCalendar, getCalendarLang, subscribeCalendarLang } from '../../lib/calendar-i18n';
import { ReservationBadge } from './ReservationBadge';

interface CalendarSidebarProps {
  // 캘린더 목록 변경 시 부모(CalendarPage)가 이벤트 재조회하도록 알림.
  onCalendarsChanged?: (calendars: CalendarDto[]) => void;
  /**
   * ⭐ 「다가올 일정」을 캘린더 목록에 «병합»한다 (2026-10-13 사용자 결정 · 옛 가지 191323cf8 · 230b2473d).
   *   넘기면 각 캘린더 체크박스 줄 «아래»에 그 캘린더의 다가올 일정이 붙는다(체크를 끄면 함께 숨는다).
   *   ⚠ 넘기지 않으면 종전과 100% 같다 — 헤더 드롭다운(셸 OFF)의 캘린더 목록이 그 경로다.
   *   ⚠ 부모가 «구간»(오늘 00:00 ~ 다음 주 끝)으로 이미 잘라서 넘긴다. 여기서는 아직 안 끝난 것만 남긴다.
   */
  upcomingEvents?: CalendarEventDto[];
  /** 다가올 일정 항목 클릭 → 그 날짜 선택(우측 레일이 상세를 보여준다). */
  onSelectUpcoming?: (date: Date) => void;
}

/** 캘린더 하나 아래에 처음 보여 줄 다가올 일정 수 — 넘치면 「더보기」로 펼친다. */
export const UPCOMING_PER_CALENDAR = 5;

// 신규 캘린더 기본 색상 팔레트 (tokens accent 계열 hex).
const PALETTE = ['#2f6df6', '#10b981', '#ec4899', '#8b5cf6', '#f97316', '#06b6d4', '#ef4444', '#eab308'];

/**
 * CalendarSidebar (그룹 G) — 다중 캘린더 목록·색상·표시 토글·생성/삭제.
 *
 * - listCalendars 로 본인 소유 캘린더 + 내가 속한 팀의 팀 캘린더 로드 (team-calendar 2026-07-10).
 *   팀 캘린더는 팀별로 묶어 보여준다. 팀 일정은 팀원 모두가 보되, 수정·삭제는 작성자만.
 * - 체크박스 토글 → calendarStore.visibleCalendarIds (null=전체).
 * - 생성: 이름 입력 + 팔레트 색상 자동 배정.
 * - 삭제: 기본 캘린더 제외(백엔드 409 가드, UI 도 버튼 숨김).
 *
 * 격리·회귀: api 는 본인 소유만 반환(T-cal37). 빈 목록이어도 동작(전체 표시 폴백).
 */
export function CalendarSidebar({ onCalendarsChanged, upcomingEvents, onSelectUpcoming }: CalendarSidebarProps) {
  useSyncExternalStore(subscribeCalendarLang, getCalendarLang, getCalendarLang);
  const visibleIds = useCalendarStore((s) => s.visibleCalendarIds);
  const toggleVisible = useCalendarStore((s) => s.toggleCalendarVisible);

  // authStore.user.id 는 number, CalendarDto.ownerId 는 string(bigint 직렬화) → 문자열로 맞춘다.
  const myUserId = useAuthStore((st) => (st.user?.id != null ? String(st.user.id) : null));
  const [calendars, setCalendars] = useState<CalendarDto[]>([]);
  // team-calendar — 생성 폼의 '소속' 선택지. 팀이 없으면 셀렉트를 아예 감춘다.
  const [teams, setTeams] = useState<TeamDto[]>([]);
  const [newTeamId, setNewTeamId] = useState<string>('');
  const [adding, setAdding] = useState(false);
  const [newName, setNewName] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // 색 고르개가 열린 캘린더 id (옛 가지 2e38c0413 의 팔레트만 가져왔다 — 자동 생성·이름 고정은 안 가져왔다).
  const [recoloring, setRecoloring] = useState<string | null>(null);
  // 「더보기」로 펼친 캘린더 id 집합(다가올 일정).
  const [expanded, setExpanded] = useState<ReadonlySet<string>>(() => new Set());

  // notify=true 일 때만 부모 refresh 트리거(생성/삭제 후). 초기 로드는 notify 안 함(중복 fetch 방지).
  async function load(notify = false) {
    try {
      const cals = await listCalendars();
      setCalendars(cals);
      if (notify) onCalendarsChanged?.(cals);
    } catch {
      // 캘린더 목록 로드 실패는 치명적 아님 — 기본(전체) 표시 유지.
      setCalendars([]);
    }
  }

  useEffect(() => {
    void load(false);
    // 팀 목록은 실패해도 무해 — 팀 캘린더 생성 옵션만 사라진다.
    listTeams().then(setTeams).catch(() => setTeams([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 색 고르개 — 바깥 누름 / Esc 로 닫는다.
  // ⚠ mousedown 은 click 보다 먼저 온다 — 고르개 «안»을 누른 것은 건드리지 않아야 색 단추의 click 이 산다.
  useEffect(() => {
    if (!recoloring) return;
    function onDoc(e: MouseEvent) {
      const t = e.target as HTMLElement | null;
      if (t && typeof t.closest === 'function' && t.closest('.calendar-cals-palette, .calendar-cals-dot--btn')) return;
      setRecoloring(null);
    }
    function onKey(e: KeyboardEvent) { if (e.key === 'Escape') setRecoloring(null); }
    window.addEventListener('mousedown', onDoc);
    window.addEventListener('keydown', onKey);
    return () => { window.removeEventListener('mousedown', onDoc); window.removeEventListener('keydown', onKey); };
  }, [recoloring]);

  /*
   * 캘린더별 다가올 일정 — 아직 안 끝난 것만, 시작 시각 오름차순.
   * ⚠ calendarId 가 없거나 목록에 없는(소속 불명) 일정은 「기본 캘린더」 통(NO_CALENDAR)으로 모은다
   *   — 그 통이 바로 그런 일정의 자리다(옛 가지는 개인 캘린더로 보냈는데 main 에는 그 통이 따로 있다).
   */
  const upcomingByCalendar = useMemo(() => {
    const map = new Map<string, CalendarEventDto[]>();
    if (!upcomingEvents || upcomingEvents.length === 0) return map;
    const now = Date.now();
    const known = new Set(calendars.map((c) => c.id));
    const rows = upcomingEvents
      .filter((e) => new Date(e.endAt).getTime() >= now)
      .sort((a, b) => new Date(a.startAt).getTime() - new Date(b.startAt).getTime());
    for (const e of rows) {
      const key = e.calendarId && known.has(e.calendarId) ? e.calendarId : NO_CALENDAR;
      const arr = map.get(key);
      if (arr) arr.push(e); else map.set(key, [e]);
    }
    return map;
  }, [upcomingEvents, calendars]);

  /*
   * ⭐ 「없음」 통을 «하나의 캘린더처럼» 센다 (2026-09-30).
   *   그 통에 체크박스가 없어서, 캘린더 하나만 꺼도 «캘린더를 안 고른 일정»이 함께
   *   사라지고 되살릴 길이 없었다. `allIds` 에 넣어 두면 「전부 켜짐 → null」 정규화도
   *   그대로 맞는다(토글 로직을 안 건드린다).
   */
  const allIds = [...calendars.map((c) => c.id), NO_CALENDAR];
  // team-calendar — 개인 캘린더가 먼저, 그다음 팀별 묶음(서버 정렬과 동일 순서 유지).
  // ⚠ `=== null` 이 아니라 falsy 검사다. 구버전 응답/테스트 mock 은 teamId 를 아예 안 보낼 수 있고,
  //   `undefined === null` 은 false 라 개인 캘린더가 팀 그룹으로 새어 들어간다.
  // org-team-calendar (migration 229 · 2026-10-13) — 조직도팀 캘린더도 teamId 는 NULL 이라
  //   orgTeamId 를 함께 봐야 «개인» 묶음으로 새지 않는다. 두 칸은 DB CHECK 로 동시에 값을 못 가진다.
  const personal = calendars.filter((c) => !c.teamId && !c.orgTeamId);
  const teamGroups = [...calendars.filter((c) => !!c.teamId || !!c.orgTeamId)
    .reduce((m, c) => {
      const key = c.teamName ?? c.orgTeamName ?? tCalendar('calendar.sidebar.team_group');
      m.set(key, [...(m.get(key) ?? []), c]);
      return m;
    }, new Map<string, CalendarDto[]>())];
  function isVisible(id: string): boolean {
    return visibleIds === null || visibleIds.includes(id);
  }

  async function handleCreate() {
    const name = newName.trim();
    if (!name || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      const color = PALETTE[calendars.length % PALETTE.length]!;
      await createCalendar({ name, color, sortOrder: calendars.length, ...(newTeamId ? { teamId: newTeamId } : {}) });
      setNewName('');
      setNewTeamId('');
      setAdding(false);
      await load(true);
    } catch (err) {
      const code = (err as any)?.response?.data?.code ?? '';
      setError(
        code === 'CALENDAR_NAME_DUPLICATE' ? tCalendar('calendar.sidebar.err_duplicate')
        : code === 'INVALID_TEAM' ? tCalendar('calendar.sidebar.err_team')
        : tCalendar('calendar.sidebar.err_create'),
      );
    } finally {
      setSubmitting(false);
    }
  }

  /** 색 바꾸기 — 본인 소유 캘린더만(서버 PATCH /calendars/:id 도 ownerId 로 막는다). */
  async function handleRecolor(cal: CalendarDto, color: string) {
    setRecoloring(null);
    if (color.toLowerCase() === (cal.color ?? '').toLowerCase()) return;
    setError(null);
    try {
      await patchCalendar(cal.id, { color });
      await load(true);
    } catch {
      setError(tCalendar('calendar.sidebar.err_recolor'));
    }
  }

  async function handleDelete(cal: CalendarDto) {
    if (cal.isDefault) return;
    if (!(await layerConfirm(tCalendar('calendar.sidebar.delete_calendar_confirm')))) return;
    try {
      await deleteCalendar(cal.id);
      await load(true);
    } catch { /* 실패 시 무시 — 다음 load 에서 정합 */ }
  }

  return (
    <div className="calendar-cals-section">
      <div className="calendar-cals-header">
        <span className="calendar-cals-title">
          {tCalendar('calendar.sidebar.calendars_heading')}
          {/* 병합 모드 — 레이아웃 참조 all-frame.html 의 「내 캘린더 · 다가올 일정」 한 덩어리 */}
          {onSelectUpcoming && (
            <>
              <span className="calendar-cals-title-sep" aria-hidden>·</span>
              <span className="calendar-cals-title-sub">{tCalendar('calendar.shell.upcoming_heading')}</span>
            </>
          )}
        </span>
        <button
          type="button"
          className="calendar-cals-add-btn"
          onClick={() => setAdding((v) => !v)}
          aria-label={tCalendar('calendar.sidebar.add_calendar')}
          title={tCalendar('calendar.sidebar.add_calendar')}
        >
          <Plus size={14} />
        </button>
      </div>

      {adding && (
        <div className="calendar-cals-add-form">
          <input
            type="text"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') void handleCreate(); if (e.key === 'Escape') setAdding(false); }}
            placeholder={tCalendar('calendar.sidebar.new_calendar_name')}
            maxLength={100}
            autoFocus
            disabled={submitting}
          />
          {teams.length > 0 && (
            <select
              className="calendar-cals-team-select"
              value={newTeamId}
              onChange={(e) => setNewTeamId(e.target.value)}
              disabled={submitting}
              aria-label={tCalendar('calendar.sidebar.new_calendar_owner')}
            >
              <option value="">{tCalendar('calendar.sidebar.owner_personal')}</option>
              {teams.map((t) => (
                <option key={t.id} value={t.id}>{t.name}</option>
              ))}
            </select>
          )}
          <button type="button" onClick={() => void handleCreate()} disabled={submitting || !newName.trim()}>
            <Check size={14} />
          </button>
        </div>
      )}
      {error && <div className="calendar-cals-error" role="alert">{error}</div>}

      {/*
        ⭐ 「기본 캘린더」 통 — `calendarId` 가 없는 일정이 여기 모인다 (2026-09-30).
        ⚠ 이름을 고르개(`EventCreateModal`)와 «같게» 둔다. 거기서 「기본 캘린더」를 보고
          만든 일정이 여기 있어야 사용자가 둘을 잇는다. 다르게 부르면 못 잇는다.
        ⚠ `calendars.isDefault` 칸과는 «다른 것»이다 — 그 칸은 아무도 참으로 안 세운다
          (만드는 곳 한 군데가 `isDefault: false` 로 박혀 있다).
      */}
      <div className="calendar-cals-group">
        <ul className="calendar-cals-list">
          <li className="calendar-cals-item">
            <label className="calendar-cals-item-label">
              <input
                type="checkbox"
                checked={isVisible(NO_CALENDAR)}
                onChange={() => toggleVisible(NO_CALENDAR, allIds)}
                aria-label={`${tCalendar('calendar.create.calendar_default')} ${tCalendar('calendar.sidebar.toggle_visible')}`}
              />
              <span className="calendar-cals-dot calendar-cals-dot--none" />
              <span className="calendar-cals-name">{tCalendar('calendar.create.calendar_default')}</span>
            </label>
            {onSelectUpcoming && isVisible(NO_CALENDAR) && renderUpcoming(NO_CALENDAR)}
          </li>
        </ul>
      </div>
      {renderGroup(personal, tCalendar('calendar.sidebar.personal_group'))}
      {teamGroups.map(([teamName, cals]) => renderGroup(cals, teamName))}
    </div>
  );

  function renderGroup(cals: CalendarDto[], heading: string) {
    if (cals.length === 0) return null;
    return (
      <div key={heading} className="calendar-cals-group">
        <div className="calendar-cals-group-heading">{heading}</div>
        <ul className="calendar-cals-list">
          {cals.map((cal) => (
            <li key={cal.id} className="calendar-cals-item">
              <label className="calendar-cals-item-label">
                <input
                  type="checkbox"
                  checked={isVisible(cal.id)}
                  onChange={() => toggleVisible(cal.id, allIds)}
                  aria-label={`${cal.name} ${tCalendar('calendar.sidebar.toggle_visible')}`}
                />
                {canRecolor(cal) ? (
                  <button
                    type="button"
                    className="calendar-cals-dot calendar-cals-dot--btn"
                    style={{ background: cal.color }}
                    aria-haspopup="true"
                    aria-expanded={recoloring === cal.id}
                    aria-label={`${cal.name} ${tCalendar('calendar.sidebar.change_color')}`}
                    title={tCalendar('calendar.sidebar.change_color')}
                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); setRecoloring((v) => (v === cal.id ? null : cal.id)); }}
                  />
                ) : (
                  <span className="calendar-cals-dot" style={{ background: cal.color }} />
                )}
                <span className="calendar-cals-name">{cal.name}</span>
                {cal.isDefault && (
                  <span className="calendar-cals-default">{tCalendar('calendar.sidebar.default_badge')}</span>
                )}
              </label>
              {/* 삭제는 캘린더를 만든 사람만. 팀 캘린더도 서버가 ownerId 로 강제한다(비소유자 404). */}
              {!cal.isDefault && (myUserId == null || cal.ownerId === myUserId) && (
                <button
                  type="button"
                  className="calendar-cals-delete"
                  onClick={() => void handleDelete(cal)}
                  aria-label={`${cal.name} ${tCalendar('calendar.detail.btn_delete')}`}
                >
                  <Trash2 size={12} />
                </button>
              )}
              {recoloring === cal.id && (
                <div className="calendar-cals-palette" role="group" aria-label={tCalendar('calendar.sidebar.change_color')}>
                  {PALETTE.map((c) => {
                    const on = c.toLowerCase() === (cal.color ?? '').toLowerCase();
                    return (
                      <button
                        key={c}
                        type="button"
                        className={`calendar-cals-palette-swatch${on ? ' is-on' : ''}`}
                        style={{ background: c }}
                        aria-pressed={on}
                        aria-label={c}
                        onClick={() => void handleRecolor(cal, c)}
                      />
                    );
                  })}
                </div>
              )}
              {/* 230b2473d — 체크를 끄면 그 캘린더의 다가올 일정도 함께 숨긴다(그리드 필터와 같은 말) */}
              {onSelectUpcoming && isVisible(cal.id) && renderUpcoming(cal.id)}
            </li>
          ))}
        </ul>
      </div>
    );
  }

  /** 색 바꾸기 권한 — 내가 만든 캘린더만(삭제와 같은 기준이되, 내 id 를 모르면 «안» 연다). */
  function canRecolor(cal: CalendarDto): boolean {
    return myUserId != null && cal.ownerId === myUserId;
  }

  /** 캘린더 줄 아래에 붙는 다가올 일정(옛 CalendarUpcoming 그룹 본문과 같은 꼴). 없으면 아무것도 안 그린다. */
  function renderUpcoming(calId: string) {
    const rows = upcomingByCalendar.get(calId) ?? [];
    if (rows.length === 0) return null;
    const open = expanded.has(calId);
    const shown = open ? rows : rows.slice(0, UPCOMING_PER_CALENDAR);
    const hidden = rows.length - shown.length;
    const toggle = () => setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(calId)) next.delete(calId); else next.add(calId);
      return next;
    });
    return (
      <div className="calendar-cals-item-upcoming" role="group" aria-label={tCalendar('calendar.shell.upcoming_heading')}>
        {shown.map((e) => {
          const start = new Date(e.startAt);
          const meta = TYPE_COLOR_MAP[e.type as CalendarEventType];
          return (
            <button
              key={`${e.id}-${e.occurrenceAt ?? ''}`}
              type="button"
              className="cal-upcoming-item"
              onClick={() => onSelectUpcoming?.(start)}
              title={`${e.title}${meta ? ` (${meta.label})` : ''}`}
            >
              <span className="cal-upcoming-date">
                {String(start.getMonth() + 1).padStart(2, '0')}.{String(start.getDate()).padStart(2, '0')}
              </span>
              <span className="cal-upcoming-title"><ReservationBadge event={e} />{e.title}</span>
            </button>
          );
        })}
        {hidden > 0 && (
          <button type="button" className="cal-upcoming-more calendar-cals-upcoming-toggle" onClick={toggle} aria-expanded={false}>
            {tCalendar('calendar.sidebar.upcoming_more', { n: hidden })}
          </button>
        )}
        {open && rows.length > UPCOMING_PER_CALENDAR && (
          <button type="button" className="cal-upcoming-more calendar-cals-upcoming-toggle" onClick={toggle} aria-expanded>
            {tCalendar('calendar.sidebar.upcoming_less')}
          </button>
        )}
      </div>
    );
  }
}
